<template>
  <x-dialog
    v-model="visible"
    width="770px"
    :title="t('discount.custom_total')"
    :before-close="handleClose"
    @close="handleClose"
    :close-on-click-modal="false"
  >
    <div class="w-[355px] flex flex-col justify-between">
      <div>
        <x-input :label="t('discount.label_target', { symbol: getCurrencySymbol() })" v-model="value" />
      </div>
      <div>
        <x-keyboard class="mb-[5px]" :single="true" @change="handleKeyboardChange" />
        <x-button type="primary" class="w-full" @click="handleConfirm">{{ t('common.confirm') }}</x-button>
      </div>
    </div>
  </x-dialog>
</template>
<script lang="ts" setup>
import { CalcType, IInvoice } from '@posx/core';
import { ref, watch, inject, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { notify } from '@/utils/notification';
import { DiscountItem } from '@/types/discount.type';
import { getCurrencySymbol } from '@/utils/formatNumber';
import { useInvoiceStore } from '@/stores/invoice';
import { useConfigStore } from '@/stores/config';

const { t } = useI18n();
const invoiceStore = useInvoiceStore();
const configStore = useConfigStore();
const invoice = inject<Ref<IInvoice | null>>('invoice', ref(null));

const $emit = defineEmits(['update:modelValue', 'close', 'confirm']);
const $props = defineProps<{
  modelValue: boolean;
  totalAmount: number;
}>();

const value = ref('0');
const visible = ref($props.modelValue);

watch(
  () => $props.modelValue,
  (newVal: boolean) => {
    visible.value = newVal;
    if (newVal) value.value = '0';
  },
);

const handleKeyboardChange = (input: string) => {
  let val = value.value;
  if (input === 'backspace') {
    val = val.slice(0, -1);
    if (val == '') val = '0';
    value.value = val;
    return;
  } else if (input === '.') {
    if (val.includes('.')) return;
    if (val === '') { value.value = '0.'; return; }
  } else if (val === '0' && input !== '.') {
    value.value = input;
    return;
  }
  value.value += input;
};

const handleClose = () => {
  $emit('update:modelValue', false);
  $emit('close', false);
};

// Calculate flat discount from target total and emit as DiscountItem
const handleConfirm = async () => {
  const targetTotal = parseFloat(value.value.toString());
  if (!$props.totalAmount || $props.totalAmount === 0) { notify.error(t('discount.error_no_items_to_discount')); return; }
  if (targetTotal >= $props.totalAmount || targetTotal < 0) { notify.error(t('discount.error_target_must_be_less')); return; }
  if (!invoice?.value) { notify.error(t('common.error')); return; } // no invoice context (e.g. opened outside pos/table)
  const invoiceService = await invoiceStore.getService();
  const discountAmount = invoiceService.calculateDiscountFromTargetTotal(invoice.value, targetTotal, configStore.appConfig!);
  $emit('confirm', Object.assign(new DiscountItem(), { name: t('discount.title'), calc_type: CalcType.Flat, amount: discountAmount, percent: 0 }));
  handleClose();
};
</script>
